import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useContext } from "react"
import { BoaterInvoicesContext } from "src/main/Account/Invoices"
import {
  invoiceSourceIcon,
  invoiceStatusClasses,
  invoiceStatusIcon,
  invoiceStatusIconStyle,
} from "src/main/Account/Invoices/InvoiceList/helpers"

import Button from "src/components/Button"

import useHover from "src/hooks/use_hover"
import useWindowSize from "src/hooks/use_window_size"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

const Invoice = ({ invoice, payNowSupported }) => {
  const { translations } = useContext(BoaterInvoicesContext)
  const [hoverRef, isHovered] = useHover()
  const { isXSScreen } = useWindowSize()

  const renderTotalDisplay = () => {
    const displayAmount = formatCurrencyFromMinorUnits({
      amount: invoice.totalAmount,
      currency: invoice.presentmentCurrency,
    })

    return <div className="font-semibold">{displayAmount}</div>
  }

  const renderStatusDisplay = () => {
    const classes = invoiceStatusClasses(invoice.settledStatus)
    const icon = invoiceStatusIcon(invoice.settledStatus)
    const iconStyle = invoiceStatusIconStyle(invoice.settledStatus)
    const text =
      invoice.settledStatus === "scheduled" && invoice.autopayEnabled
        ? translations.autopayEnabled
        : invoice.settledStatusDisplay

    return (
      <span className={`flex items-center font-semibold ${classes}`}>
        {icon ? <i className={`fa-${iconStyle} ${icon} mr-1`} /> : null}
        <span className="text-sm">{text}</span>
      </span>
    )
  }

  const handlePayNowClick = () => {
    window.open(invoice.payNowPath, "_self")
  }

  const renderPayNowButton = () => {
    return payNowSupported && invoice.boaterPayable ? (
      <Button
        variant="primary"
        onClick={(event) => {
          event.stopPropagation()
          handlePayNowClick()
        }}
      >
        {translations.payNow}
      </Button>
    ) : null
  }

  return (
    <div className="py-2">
      <div
        className={classNames(
          "grid grid-cols-5 gap-3 rounded px-2 py-4 sm:gap-10",
          {
            "cursor-pointer bg-gray-100": isHovered,
          }
        )}
        onClick={() => window.open(invoice.detailsPath, "_self")}
        ref={hoverRef}
      >
        <div className="col-span-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div className="col-span-1 flex gap-3 sm:col-span-2">
              <div className="hidden sm:inline">
                <div className="flex h-8 w-8 items-center rounded bg-gray-200">
                  <i
                    className={`fa-kit fa-${invoiceSourceIcon(
                      invoice.parentRecordKind
                    )} mx-auto text-xl`}
                  />
                </div>
              </div>
              <div>
                <div className="font-semibold">{invoice.marinaName}</div>
                <div className="text-sm">
                  {`${invoice.parentRecordType} #${invoice.parentRecordId}`}
                </div>
              </div>
            </div>
            <div className="col-span-1 sm:col-span-2">
              <div
                className={classNames("", {
                  "flex items-center gap-2": isXSScreen,
                })}
              >
                {renderTotalDisplay()}
                <div className="text-sm">{`Due ${invoice.dueDate}`}</div>
              </div>
              <div>{renderStatusDisplay()}</div>
            </div>
          </div>
        </div>
        <div className="col-span-1 flex items-center justify-end gap-3">
          {renderPayNowButton()}
          <div className="hidden sm:inline">
            <i className="fa-kit fa-chevron text-xl text-gray-600" />
          </div>
        </div>
      </div>
    </div>
  )
}

Invoice.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.string.isRequired,
    autopayEnabled: PropTypes.bool.isRequired,
    boaterPayable: PropTypes.bool.isRequired,
    detailsPath: PropTypes.string.isRequired,
    dueDate: PropTypes.string.isRequired,
    marinaName: PropTypes.string.isRequired,
    parentRecordId: PropTypes.string.isRequired,
    parentRecordType: PropTypes.string.isRequired,
    parentRecordKind: PropTypes.string.isRequired,
    payNowPath: PropTypes.string.isRequired,
    presentmentCurrency: PropTypes.string.isRequired,
    settledStatus: PropTypes.string.isRequired,
    settledStatusDisplay: PropTypes.string.isRequired,
    totalAmount: PropTypes.number.isRequired,
  }).isRequired,
  payNowSupported: PropTypes.bool.isRequired,
}

export default Invoice
